import http from "@/http/http"
import type { BusinessPointProfileVO } from "@/api"
// ==============================================
interface SceneListArgs {
  poiId: string
  areaCode: string // 城市id
  brandId: string // 品牌id
  moduleType: string // 模块名称（IndustryModuleType）
  sceneCustomerType: string // 场景拓客类型（SceneCustomerType）
  queryType: string // 筛选类型（SceneCustomerQueryType）

  cooStatus?: boolean // 合作状态
  businessType?: string // 商户类型（SceneCustomerBusinessTag）

  // queryType==CITY_RANGE时传参
  districtId?: string // 区县
  streetId?: string // 街道

  // queryType==STORE时传参
  latitude?: number // 中心点纬度
  longitude?: number // 中心点经度
  radius?: number // 半径

  housePriceRange?: number[][] // 房价区间
  estateManagementCompanies?: string[] // 小区物业

  capitalRange?: number[][] // 注册资本
  capitalDateRange?: string[][] // 注册时间
  cppRecommend?: string // 企业联系方式（CompanyPriorityRecommendType）

  industryL1Name?: string[] // 一级行业name筛选
  industryL2Name?: string[] // 二级行业name筛选
  industryL3Name?: string[] // 三级行业name筛选

  visitFlow?: number // 到访客流
  residentialFlow?: number // 常驻客流
  flowFilterType?: string // 客流过滤方式（FlowFilterUnit）

  sortType: string[] // 排序类型（SceneSortType）
  isAsc: boolean[] // true-升序，false-降序

  page: number // 页码
  limit: number // 每页数量
}

export interface SceneList {
  name: string // 名字
  openingDate: string // 开业时间/成立日期
  capital: string // 注册资本
  address: string // 地址
  latitude: number // 纬度
  longitude: number // 经度
  poiId: string // poiId
  cooStatusId: string | null // 合作状态id
  cooStatus: string // 合作状态（BusinessCooStatus）
  companyCount: number // 企业总数
  industryL1: string // 所属行业 -- 一级行业字段
  industryL2: string // 所属行业 -- 二级行业字段
  industryL3: string // 所属行业 -- 三级行业字段
  /**
   * 物业合作
   */
  housePrice: number // 房价
  residentPeopleCount: number // 小区人口数
  aoiPolygon: number[][] // aoi围栏
  /**
   * 商户收单
   */
  businessType: string // 商户类型
  visitFlow: number // 到访客流
  residentFlow: number // 常驻人口
  /**
   * 本地添加的字段
   */
  color?: string
}

interface SceneListResult {
  list: SceneList[]
  total: number
}

// 获取小区列表
export async function getEstateList(params: SceneListArgs): Promise<SceneListResult> {
  try {
    const result = await http.post<SceneListResult>(`/sceneExpand/scene/estate`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface SceneStatusArgs {
  // 城市id
  areaCode: string
  // 品牌id
  brandId: string
  // poiId
  poiId: string
  // 合作状态（BusinessCooStatus）
  cooStatus: string
}

// 更改小区合作状态
export async function changeSceneCooStatus(params: SceneStatusArgs): Promise<string | null> {
  try {
    const result = await http.post<string | null>(`/sceneExpand/scene/estate/maintain`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface QueryPropertyArgs {
  // 城市id
  areaCode: string
  // 筛选类型
  queryType: string
  // 搜索词
  query: string
  // 区县id
  // districtId?: string
  // 街道id
  streetId?: string
  // 纬度
  latitude?: number
  // 经度
  longitude?: number
  // poiId
  poiId?: string
  // 半径
  radius?: number
  // 房价区间
  housePriceRange?: number[][]
}

export interface QueryPropertyVO {
  name: string // 物业公司名字
  estateCount: number // 小区数量
}

// 查询小区物业
export async function queryEstatePropertyCompany(
  params: QueryPropertyArgs,
): Promise<QueryPropertyVO[]> {
  try {
    const result = await http.post<QueryPropertyVO[]>(`/sceneExpand/scene/estate/property`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface EstateProfileArgs {
  areaCode: string // 城市id
  brandId: string // 品牌id
  moduleType: string // 模块名称（IndustryModuleType）
  sceneCustomerType: string // 场景拓客类型（SceneCustomerType）
  poiId: string // poiId
  latitude: number // 纬度
  longitude: number // 经度
  centralLatitude?: number | null // 中心点纬度
  centralLongitude?: number | null // 中心点经度
  profileTag: string
}

interface EstateEcologyInfo {
  address: string
  buildingArea: string
  buildingCount: number
  buildingStructure: string
  carParkingCount: number
  carParkingRatio: number
  completionDate: number
  developmentCompany: string
  ecologyType: string
  floorInfo: string
  greeningRate: number
  housePrice: number
  name: string
  propertyCompany: string
  propertyPrice: number
  propertyRight: string
  residentCount: number
}

interface EstateProfileResult {
  name: string
  dataDate: string
  // 生态信息
  ecologyInfo: EstateEcologyInfo
  // 人群分析
  populationAnalysis: BusinessPointProfileVO
}

// 小区场景—画像分析
export async function estateScenePointProfile(
  params: EstateProfileArgs,
): Promise<EstateProfileResult> {
  try {
    const { profileTag, ...others } = params
    const result = await http.post<EstateProfileResult>(
      `/sceneExpand/scene/estate/profile/${profileTag}`,
      others,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}
